import React, { useEffect, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { Loader2, BookOpen, ArrowLeft } from 'lucide-react';

// Internal/test author UID — never surfaced to readers.
const TEST_AUTHOR_ID = 'rkfAMFsjezU5SgVj8eiU7PvsgJM2';

export default function SelectAuthorPage() {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = 'cormorant-garamond-font';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    async function loadAuthors() {
      try {
        const authorsSnap = await getDocs(collection(db, 'authors'));
        const list = [];
        for (const authorDoc of authorsSnap.docs) {
          // Hard exclude the internal/test account by ID before any other logic.
          if (authorDoc.id === TEST_AUTHOR_ID) continue;

          const data = authorDoc.data() || {};

          // Find an active book with a real cover. Skip the author entirely
          // if they have none — this filters out stray/test accounts.
          const JULIEANNA_ID = 'uzMXPEok4AVIw8luvjAeNLlWy743';
          const BORN_FROM_THE_BROKEN_FALLBACK =
            'https://images.squarespace-cdn.com/content/68247682061cc776f130e7ed/552ee632-8a04-43da-b258-118af34f36ae/9781918400045.png?content-type=image%2Fpng';

          let bookCover = '';
          let bookTitle = '';
          try {
            const booksSnap = await getDocs(collection(db, 'authors', authorDoc.id, 'books'));
            const books = booksSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const active = books.filter(b => b.status !== 'archived' && b.status !== 'draft');
            if (active.length === 0) continue;

            // Prefer launched > pre_launch_active > first active, regardless of coverUrl.
            const preferred =
              active.find(b => b.status === 'launched') ||
              active.find(b => b.status === 'pre_launch_active') ||
              active[0];
            const picksCover = typeof preferred.coverUrl === 'string' ? preferred.coverUrl.trim() : '';
            // If preferred lacks a cover, try any other active book with one.
            const anyCover = picksCover ||
              (active.map(b => (typeof b.coverUrl === 'string' ? b.coverUrl.trim() : '')).find(Boolean) || '');
            // Special fallback: Julieanna's book1 has an empty coverUrl field.
            bookCover = anyCover || (authorDoc.id === JULIEANNA_ID ? BORN_FROM_THE_BROKEN_FALLBACK : '');
            bookTitle = preferred.title || '';

            if (!bookCover) continue; // still nothing to show — skip stray accounts
          } catch {
            continue;
          }

          // Branding: fetch the single `branding` doc directly (public under rules).
          let branding = {};
          try {
            const brandingSnap = await getDoc(doc(db, 'authors', authorDoc.id, 'settings', 'branding'));
            if (brandingSnap.exists()) branding = brandingSnap.data() || {};
          } catch { /* skip */ }
          const name = branding.displayName || data.penName || data.name || data.displayName || 'Author';

          list.push({
            id: authorDoc.id,
            name,
            bio: branding.bio || data.bio || '',
            logo: branding.logo || data.photoURL || '',
            banner: branding.banner || '',
            bookCover,
            bookTitle,
          });
        }
        list.sort((a, b) => a.name.localeCompare(b.name));
        setAuthors(list);
      } catch (err) {
        console.error('Failed to load authors:', err);
      } finally {
        setLoading(false);
      }
    }
    loadAuthors();
  }, []);

  const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

  const hrefFor = (authorId) => `/reader/portal?authorId=${encodeURIComponent(authorId)}`;

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-slate-950 via-[#0d1117] to-slate-950 text-white flex flex-col"
      style={serif}
    >
      <div className="flex-1 flex flex-col items-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-5xl">
          {/* Header / branding */}
          <div className="text-center">
            <h1
              className="text-5xl sm:text-6xl md:text-7xl font-semibold tracking-wide bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 bg-clip-text text-transparent drop-shadow-[0_2px_12px_rgba(217,165,75,0.25)]"
              style={serif}
            >
              NARRATORIA
            </h1>
            <p className="mt-3 text-base sm:text-lg italic text-slate-300 tracking-wide" style={serif}>
              Choose an author to enter their world
            </p>
            <div className="mx-auto mt-6 flex items-center justify-center gap-3">
              <span className="h-px w-16 sm:w-24 bg-gradient-to-r from-transparent to-amber-500/60" />
              <span className="h-1.5 w-1.5 rotate-45 bg-amber-400/80" />
              <span className="h-px w-16 sm:w-24 bg-gradient-to-l from-transparent to-amber-500/60" />
            </div>
          </div>

          {/* Authors grid */}
          <div className="mt-12">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-amber-400/80" />
              </div>
            ) : authors.length === 0 ? (
              <div className="mx-auto max-w-md rounded-2xl border border-slate-800 bg-slate-900/60 p-10 text-center">
                <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-500" />
                <p className="text-slate-300 font-sans">No authors available yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                {authors.map(author => (
                  <a
                    key={author.id}
                    href={hrefFor(author.id)}
                    data-author-id={author.id}
                    className="group relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-900/10 via-slate-900/70 to-slate-950/90 text-left shadow-xl shadow-amber-900/20 transition-all hover:border-amber-400 hover:shadow-amber-500/20 hover:-translate-y-0.5 block no-underline"
                  >
                    <div className="relative h-56 overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-[#0d1117]">
                      {author.bookCover ? (
                        <>
                          {/* Blurred cover as ambient background */}
                          <img
                            src={author.bookCover}
                            alt=""
                            aria-hidden
                            className="absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-40"
                          />
                          {/* Centred cover */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <img
                              src={author.bookCover}
                              alt={author.bookTitle || author.name}
                              className="h-48 w-32 rounded-lg object-cover shadow-2xl shadow-black/60 ring-1 ring-amber-500/20"
                            />
                          </div>
                        </>
                      ) : author.banner ? (
                        <img src={author.banner} alt="" className="h-full w-full object-cover opacity-80" />
                      ) : null}
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-slate-950/95 to-transparent" />
                    </div>
                    <div className="relative px-6 pb-6 pt-10">
                      <div className="absolute -top-8 left-6">
                        {author.logo ? (
                          <img
                            src={author.logo}
                            alt={author.name}
                            className="h-16 w-16 rounded-full border-4 border-slate-950 object-cover shadow-lg shadow-amber-900/30"
                          />
                        ) : (
                          <div className="flex h-16 w-16 items-center justify-center rounded-full border-4 border-slate-950 bg-gradient-to-br from-amber-400 to-amber-700 text-2xl font-semibold text-slate-950 shadow-lg shadow-amber-900/30" style={serif}>
                            {author.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <h2 className="text-2xl font-semibold text-white group-hover:text-amber-200 transition-colors" style={serif}>
                        {author.name}
                      </h2>
                      {author.bio && (
                        <p className="mt-2 line-clamp-3 text-sm text-slate-400 font-sans leading-relaxed">
                          {author.bio}
                        </p>
                      )}
                      <div className="mt-4 inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-amber-400/80 font-sans group-hover:text-amber-300">
                        Enter portal
                        <span aria-hidden>→</span>
                      </div>
                    </div>
                    <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400 to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={() => { window.location.href = '/reader'; }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900/70 px-5 py-2.5 text-sm text-slate-200 hover:bg-slate-800 font-sans"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-800/80 py-6 px-4 text-center">
        <p className="text-xs text-slate-500 font-sans">Narratoria — Where stories find their readers</p>
      </div>
    </div>
  );
}
